import { getDesignSystemByPointOfSale } from "@/services/designSystem/designSystem.catalog.service";

/**
 * Módulo Vuex do Design System (multi-tenant).
 *
 * O design system é por loja (pdvId). Para evitar bater no endpoint a cada
 * navegação — e, mais importante, para NUNCA devolver dados de outra loja — o
 * cache é indexado por pdvId em dois níveis:
 *   1. state em memória (sobrevive à navegação dentro do SPA);
 *   2. espelho em localStorage sob a chave `designSystem:<pdvId>` (sobrevive a
 *      reload/nova aba).
 * Como a chave inclui o pdvId, trocar de loja jamais lê o cache errado; o
 * mismatch `state.pdvId !== pdvId` também força um refetch.
 */

/** Tempo de vida do cache — 15 minutos. */
const TTL_MS = 15 * 60 * 1000;

const LS_KEY = (pdvId) => `designSystem:${pdvId}`;

/**
 * Representa "ainda não carregado". Os campos ficam vazios de propósito: cada
 * tela já tem o seu próprio fallback (cor do botão, nome da loja, etc.), então
 * o store não impõe uma cor placeholder que sobrescreveria esses defaults.
 */
const DEFAULT_CONFIG = {
  hexa: "",
  logo: "",
  corporateName: "",
  phone: "",
};

function isFresh(fetchedAt) {
  return typeof fetchedAt === "number" && Date.now() - fetchedAt < TTL_MS;
}

/** Normaliza a resposta do endpoint para o shape consumido pelas telas. */
function normalizeConfig(data) {
  const designSystemConfig = data?.designSystemConfig;
  const pointOfSale = data?.pointOfSale;
  return {
    hexa: designSystemConfig?.hexa || "",
    logo: designSystemConfig?.logo || "",
    corporateName: pointOfSale?.corporateName || "",
    phone: pointOfSale?.phone || "",
  };
}

/** Lê o espelho do localStorage se ainda for válido para o pdvId pedido. */
function readCache(pdvId) {
  try {
    const raw = localStorage.getItem(LS_KEY(pdvId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.pdvId === pdvId && parsed?.config && isFresh(parsed?.fetchedAt)) {
      return parsed;
    }
  } catch (_) {
    // JSON inválido / storage indisponível — ignora e refaz o fetch.
  }
  return null;
}

const state = {
  pdvId: null,
  config: null,
  fetchedAt: null,
};

const getters = {
  getDesignSystem: (state) => state.config || { ...DEFAULT_CONFIG },
  getHexa: (state) => state.config?.hexa || "",
  getLogo: (state) => state.config?.logo || "",
  getCorporateName: (state) => state.config?.corporateName || "",
};

const mutations = {
  SET(state, { pdvId, config, fetchedAt }) {
    state.pdvId = pdvId;
    state.config = config;
    state.fetchedAt = fetchedAt;
    try {
      localStorage.setItem(
        LS_KEY(pdvId),
        JSON.stringify({ pdvId, config, fetchedAt }),
      );
    } catch (_) {
      // Storage cheio/indisponível — o cache em memória ainda funciona.
    }
  },
};

const actions = {
  /**
   * Garante o design system da loja `pdvId` em cache e o retorna.
   *
   * @param {{ pdvId: string, force?: boolean }} payload
   * @returns {Promise<Object|null>} config `{ hexa, logo, corporateName, phone }`
   */
  async fetchDesignSystem({ commit, state }, { pdvId, force = false } = {}) {
    if (!pdvId) return null;

    // 1. Cache em memória (mesma loja e ainda fresco).
    if (
      !force &&
      state.pdvId === pdvId &&
      state.config &&
      isFresh(state.fetchedAt)
    ) {
      return state.config;
    }

    // 2. Espelho no localStorage (reload/nova aba) — sem endpoint.
    if (!force) {
      const cached = readCache(pdvId);
      if (cached) {
        commit("SET", cached);
        return cached.config;
      }
    }

    // 3. Endpoint.
    const data = await getDesignSystemByPointOfSale(pdvId);
    const config = normalizeConfig(data);
    commit("SET", { pdvId, config, fetchedAt: Date.now() });
    return config;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
